// ABOUTME: Text input that reads a number in the user's locale ("4.500,00", "€ 1 234,50") and
// ABOUTME: reports it canonical, for the ad-hoc price editors that are not schema-driven forms.

import { TextField, TextFieldProps, Tooltip } from '@ds/coolshop';
import { parseLocaleNumber } from '@src/utils/generic';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

// Grouped display never invents precision: 8 decimals is the same ceiling the XLSX export uses.
const MAX_DISPLAYED_DECIMALS = 8;

export type LocaleNumberFieldProps = Omit<TextFieldProps, 'type' | 'value' | 'onChange'> & {
    value: string | number | null | undefined;
    /** canonical value ('.' decimal, no grouping), or '' when the field is cleared */
    onChange: (value: string) => void;
};

export const LocaleNumberField = ({
    value,
    onChange,
    onBlur,
    error,
    helperText,
    ...rest
}: LocaleNumberFieldProps) => {
    const intl = useIntl();

    const asText = (val: string | number | null | undefined) =>
        val === null || val === undefined ? '' : String(val);

    // Stored values are canonical ('.' decimal, no grouping), so they are read with Number and
    // never with the locale parser: under `it` the latter would take the '.' of "0.125" for a
    // thousands mark. The locale parser is for what the user types, nothing else.
    const fromStored = (val: string | number | null | undefined) => {
        const text = asText(val);
        if (text.trim() === '') {
            return null;
        }
        const parsed = Number(text);
        return Number.isFinite(parsed) ? parsed : null;
    };

    // These fields only ever carry amounts, so the digits are always grouped: the opt-out of the
    // schema-driven number field, for years and ordinals, has no equivalent here.
    const toDisplayValue = (val: string | number | null | undefined) => {
        const parsed = fromStored(val);
        return parsed === null
            ? asText(val)
            : intl.formatNumber(parsed, {
                  useGrouping: true,
                  maximumFractionDigits: MAX_DISPLAYED_DECIMALS,
              });
    };

    const [inputValue, setInputValue] = useState(() => toDisplayValue(value));
    const [isUnreadable, setIsUnreadable] = useState(false);
    // last value handed to the parent: tells an external change apart from our own
    const lastReportedRef = useRef(asText(value));

    useEffect(() => {
        const incoming = asText(value);
        if (incoming !== lastReportedRef.current) {
            lastReportedRef.current = incoming;
            setIsUnreadable(false);
            setInputValue(toDisplayValue(incoming));
        }
    }, [value]);

    // "12," or "-" is a number being typed, not a broken one: an entry counts as still in
    // progress while appending a digit would make it readable. Flagging those mid-keystroke
    // made the field flash red between one character and the next.
    const isInProgress = (text: string) => parseLocaleNumber(`${text}0`, intl.locale) !== null;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value;
        setInputValue(text);

        if (text.trim() === '') {
            setIsUnreadable(false);
            lastReportedRef.current = '';
            onChange('');
            return;
        }

        const parsed = parseLocaleNumber(text, intl.locale);
        if (parsed !== null) {
            setIsUnreadable(false);
            const canonical = String(parsed);
            // nothing changed for the parent: skip the update and the render it costs
            if (canonical !== lastReportedRef.current) {
                lastReportedRef.current = canonical;
                onChange(canonical);
            }
            return;
        }
        setIsUnreadable(!isInProgress(text));
    };

    return (
        // The message is a tooltip, so it never takes layout space and is never clipped: these
        // fields sit in dense rows, grid cells and popovers that must neither move when a value
        // turns invalid nor cut the message off.
        <Tooltip
            open={isUnreadable}
            placement="bottom"
            disableHoverListener
            title={intl.formatMessage({
                id: 'field.invalidNumber',
                defaultMessage: 'Invalid number',
            })}
        >
            <TextField
                {...rest}
                // a plain text input: type="number" makes the browser strip the separators of a
                // pasted "4.500,00" before the value ever reaches us
                type="text"
                inputRef={(input: HTMLInputElement | null) =>
                    input?.setAttribute('inputmode', 'decimal')
                }
                value={inputValue}
                onChange={handleChange}
                onBlur={event => {
                    // "12," is a decimal the user stopped typing halfway: it means 12. Anything
                    // still unreadable is only reported once the user leaves the field.
                    const settled = inputValue.replace(/[.,\s]+$/, '');
                    const parsed = settled === '' ? null : parseLocaleNumber(settled, intl.locale);
                    const unreadable = inputValue.trim() !== '' && parsed === null;
                    setIsUnreadable(unreadable);
                    if (parsed !== null && String(parsed) !== lastReportedRef.current) {
                        lastReportedRef.current = String(parsed);
                        onChange(lastReportedRef.current);
                    }
                    if (!unreadable) {
                        setInputValue(toDisplayValue(lastReportedRef.current));
                    }
                    onBlur?.(event);
                }}
                error={error || isUnreadable}
                helperText={helperText}
            />
        </Tooltip>
    );
};
